import { escapeHtml } from "../site-search/search-escaping.js";

/**
 * A label written in both languages, which is how 复习 says everything.
 *
 * **The English leads and the Chinese follows, smaller and quieter.** It is
 * the treatment the navigation bar gives its own entries and the Pro panel
 * gives 专业, and it is the right way round for a reader who is learning the
 * Chinese rather than reading it. Somebody who knows the word reads past the
 * English, and somebody who does not has it.
 *
 * **The Chinese carries `lang`, and that is not decoration.** It puts the text
 * in LXGW WenKai through `components/hanzi`, tells a screen reader to change
 * voice, and lets `reader-settings` reach it. A page that mixes the two
 * languages without saying which is which is read aloud in one accent.
 *
 * The pair is one element with two spans rather than two elements, so a label
 * wraps as a unit and `modules/fuxi` can space the halves with one rule.
 */

/** The script the site writes its own Chinese in. */
const chineseLanguage = "zh-Hans";

/** The script a reader on zh-TW or zh-HK has asked for. */
export const traditionalLanguage = "zh-Hant";

/** The script the site writes its own Chinese in, for anything tagging both. */
export const simplifiedLanguage = chineseLanguage;

/**
 * A label whose two writings are different words.
 *
 * 设置 and 設定 are not one word converted character by character, and neither
 * are 复习 and 複習. A label that differs is written twice and tagged, and
 * `components/reader-settings` shows the one the reader chose.
 *
 * A label written alike in both, 例句 and 退出 among them, stays a plain
 * string. It carries no attribute and every reader gets it, which is the
 * arrangement the 词典 uses for an entry that has one writing.
 */
export interface ChineseWritings {
  /** 简体, which is what a reader who has chosen nothing is shown. */
  readonly simplified: string;

  /** 繁體, for zh-TW and zh-HK alike. */
  readonly traditional: string;
}

/**
 * What `components/reader-settings` matches a choice against.
 *
 * The three settings are `zh-CN`, `zh-TW` and `zh-HK`, and the rules there
 * match with `~=`, so one element serves both 繁體 readers by naming both.
 * `$writings` in that file is the same list.
 */
export const simplifiedWriting = "zh-CN";
export const traditionalWritings = "zh-TW zh-HK";

/** One writing, as the span that carries it. */
function sayChinese(
  chinese: string,
  language: string,
  writing?: string,
): string {
  const tag = writing === undefined ? "" : ` data-cb-writing="${writing}"`;

  return `<span class="cb-say-zh" lang="${language}"${tag}>${escapeHtml(chinese)}</span>`;
}

/**
 * The Chinese half of a pair, in one writing or in both.
 *
 * Exported for a line whose English half is drawn some other way, such as one
 * opening with a Chinese term. See {@link englishOpening}.
 */
export function chineseHalf(chinese: string | ChineseWritings): string {
  return typeof chinese === "string"
    ? sayChinese(chinese, chineseLanguage)
    : sayChinese(chinese.simplified, chineseLanguage, simplifiedWriting) +
        sayChinese(
          chinese.traditional,
          traditionalLanguage,
          traditionalWritings,
        );
}

/**
 * One label in both languages.
 *
 * @param english - The label, which leads.
 * @param chinese - The same label in Chinese, which follows it. A string where
 *   both writings are alike, and a {@link ChineseWritings} where they differ.
 * @returns The pair, ready to go inside any element.
 */
export function bilingual(
  english: string,
  chinese: string | ChineseWritings,
): string {
  return `<span class="cb-say-en">${escapeHtml(english)}</span>${chineseHalf(chinese)}`;
}

/**
 * The same pair as one line of text, for a title or an `aria-label`.
 *
 * A browser tab and an accessible name are both plain strings, so the two
 * halves are joined by a space and the markup goes.
 *
 * A tab is one string and a reader's choice is applied in the page, so the
 * 简体 writing is the one that goes here. The heading under it says the same
 * label in whichever writing the reader asked for.
 *
 * @param english - The label, which leads.
 * @param chinese - The same label in Chinese, which follows it.
 * @returns The pair as text.
 */
export function bilingualText(
  english: string,
  chinese: string | ChineseWritings,
): string {
  return `${english} ${typeof chinese === "string" ? chinese : chinese.simplified}`;
}

/**
 * A Chinese term standing inside an English sentence.
 *
 * 复习 is this feature's name, and its name is Chinese, so English prose about
 * it has Chinese in it. Without a `lang` of its own that run sits inside
 * `lang="en-GB"` content, and a screen reader reads two 汉字 in an English
 * voice. The face is a separate matter and would have come out right either
 * way, `$body-font` splicing the Chinese family into its own fallback chain.
 *
 * The answer is markup rather than text, so it cannot be handed to
 * {@link bilingual}, which escapes what it is given. {@link englishOpening} is
 * how the two are put together.
 *
 * @param term - The Chinese, as text.
 * @returns The term, tagged as Chinese.
 */
export function chineseTerm(term: string): string {
  return `<span lang="${chineseLanguage}">${escapeHtml(term)}</span>`;
}

/**
 * An English sentence opening with a Chinese term.
 *
 * The shape every such sentence on this page takes, 复习 being the subject of
 * all of them. The term is tagged and the rest is escaped, which is the whole
 * of the difference from writing the sentence out.
 *
 * An empty term is a sentence with no Chinese in it, and draws no wrapper. It
 * is the commoner case of the two, and a sentence carrying an empty element is
 * a sentence carrying something to explain later.
 *
 * @param term - The Chinese the sentence opens with, where it opens with one.
 * @param rest - The English after it, which is escaped.
 * @returns The sentence, ready to go inside any element.
 */
export function englishOpening(term: string, rest: string): string {
  return term === ""
    ? escapeHtml(rest)
    : `${chineseTerm(term)}${escapeHtml(rest)}`;
}
